
import React, { useCallback, useState, useEffect } from "react";
import Heading from "../../components/ui/Heading";
import SearchInput from "../../components/ui/SearchInput";
import { PlusCircle, FileEdit, X } from "lucide-react";
import api from "../../api/api";
import { getSessionUsername } from "../../utils/sessionUser";

/* ---------------- ICONS ---------------- */
const TimesIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

/* ---------------- MODAL ---------------- */
const Modal = ({ title, isOpen, onClose, children }) => {
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center w-full h-full p-4 overflow-y-auto bg-black/50">
      <div className="relative w-full max-w-lg p-6 mx-auto overflow-auto bg-white rounded-lg shadow-lg max-h-[90vh]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-bold">{title}</h3>
          <button onClick={onClose} aria-label="Close modal">
            <TimesIcon />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

/* ---------------- BUTTON ---------------- */
const Button = ({
  children,
  onClick,
  type = "button",
  disabled = false,
  className = "",
}) => {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`${className} ${disabled ? "opacity-50 cursor-not-allowed" : ""
        }`}
    >
      {children}
    </button>
  );
};
const today = new Date().toISOString().split("T")[0]; // yyyy-mm-dd
const normalizeMode = (mode) => String(mode || "").trim().toUpperCase();
const getDefaultMode = (paymentModes) => paymentModes[0]?.value || "";
const getBankId = (data) => data?.Bankid ?? data?.BankId ?? data?.BANKID ?? data?.bANKID ?? "";
const normalizePaymentModes = (data) => {
  const table2 = data?.Table2 ?? [];
  if (table2.length > 0) {
    return table2
      .map((item) => ({
        label: item.PAYMODE,
        value: normalizeMode(item.PAYMODE),
        bankId: item.bANKID ?? item.BANKID ?? item.BankId ?? "",
      }))
      .filter((item) => item.value);
  }

  return (data?.Table1 ?? [])
    .map((item) => ({
      label: item.Mode,
      value: normalizeMode(item.Mode),
      bankId: item.bANKID ?? item.BANKID ?? item.BankId ?? "",
    }))
    .filter((item) => item.value);
};
const getInitialModeValue = (initialData, paymentModes) => {
  const bankId = String(getBankId(initialData));
  if (bankId) {
    const modeByBank = paymentModes.find((item) => String(item.bankId) === bankId);
    if (modeByBank) return modeByBank.value;
  }

  const mode = normalizeMode(initialData?.Mode);
  if (!mode) return getDefaultMode(paymentModes);

  const exactMode = paymentModes.find((item) => item.value === mode);
  if (exactMode) return exactMode.value;

  const partialMode = paymentModes.find((item) => item.value.startsWith(mode) || mode.startsWith(item.value));
  return partialMode?.value || mode;
};

const ReceiptForm = ({ isOpen, onClose, onSave, mode, initialData }) => {
  const [formData, setFormData] = useState({
    Vrid: "",
    Vrno: "",
    VrDate: today,
    Vid: "",
    VName: "",
    VCode: "",
    Amount: "", // Amount stays empty initially
    Mode: "",
    Chqno: "",
    Notes: "",
    Balance: 0, // Prev.Balance
  });
  const [Suppilers, setSuppilers] = useState([]);
  const [paymentModes, setPaymentModes] = useState([]);
  const [SuppilerSearch, setSuppilerSearch] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loadingSuppilers, setLoadingSuppilers] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  function toDateOnly(isoString = "") {
    // guard against null/undefined
    return isoString ? isoString.split("T")[0] : "";
  }
  const username = getSessionUsername();
  const resetForm = useCallback(() => {
    const defaultMode = getDefaultMode(paymentModes);
    setFormData({
      Vrid: "",
      Vrno: "",
      VrDate: today,
      Vid: "",
      VName: "",
      VCode: "",
      Amount: "",
      Mode: defaultMode,
      Chqno: "",
      Notes: "",
      Balance: 0,
    });
    setSuppilerSearch("");
    setIsDropdownOpen(false);
  }, [paymentModes]);

  useEffect(() => {
    if (isOpen && mode === "add") {
      resetForm();
    }
  }, [isOpen, mode, resetForm]);


  useEffect(() => {
    if (mode === "edit" && initialData) {
      setFormData(prev => ({
        ...prev,
        Vrid: initialData.Vrid,
        Vrno: initialData.Vrno,
        // 🔑 Convert here:
        VrDate: initialData.VrDate
          ? toDateOnly(initialData.VrDate)
          : today,
        Vid: initialData.VId,
        VName: initialData.VName,
        VCode: initialData.VCode,
        Amount: initialData.CDue,
        Mode: getInitialModeValue(initialData, paymentModes),
        Chqno: initialData.ChequeNo,
        Notes: initialData.Remark,
        Balance: initialData.Balance,
      }));
    }
  }, [mode, initialData, paymentModes]);

  useEffect(() => {
    const fetchSuppilers = async () => {
      try {
        const data = await api.SupplierListWithBalance(username);
        const SuppilersTable = data?.Table ?? [];
        const modes = normalizePaymentModes(data);
        setSuppilers(SuppilersTable);
        setPaymentModes(modes);

        if (mode === "edit" && initialData) {
          const Suppiler = SuppilersTable.find((c) => c.Vid === initialData.VId);
          if (Suppiler) setSuppilerSearch(Suppiler.Vname);

          setFormData({
            Vrid: initialData.Vrid,
            Vrno: initialData.Vrno,
            VrDate: initialData.VrDate
              ? toDateOnly(initialData.VrDate)
              : today,
            Vid: initialData.VId,
            VName: initialData.VName,
            VCode: initialData.VCode,
            Amount: initialData.VrNetTotal || "", // Keep Amount from existing data
            Mode: getInitialModeValue(initialData, modes),
            Chqno: initialData.ChequeNo,
            Notes: initialData.Remark,
            Balance: initialData.Balance || 0,
          });
        } else if (mode === "add") {
          const defaultMode = getDefaultMode(modes);
          // RESET completely on "New"
          setFormData({
            Vrid: "",
            Vrno: "",
            VrDate: today,
            Vid: "",
            VName: "",
            VCode: "",
            Amount: "", // Amount empty
            Mode: defaultMode,
            Chqno: "",
            Notes: "",
            Balance: 0, // Prev.Balance empty
          });
          setSuppilerSearch(""); // Clear search
        }
      } catch (err) {
        console.error("Failed to fetch Suppilers:", err);
      } finally {
        setLoadingSuppilers(false);
      }
    };

    fetchSuppilers();
  }, [mode, initialData, username]);

  const filteredSuppilers = Suppilers.filter((c) =>
    c.Vname.toLowerCase().includes(SuppilerSearch.toLowerCase())
  );

  const handleSuppilerSelect = (Suppiler) => {
    setFormData((prev) => ({
      ...prev,
      Vid: Suppiler.Vid,
      VName: Suppiler.Vname,
      VCode: Suppiler.VCode,
      Balance: Suppiler.Balance, // Only bind Prev.Balance
    }));
    setSuppilerSearch(Suppiler.Vname);
    setIsDropdownOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "Amount" && !/^\d*\.?\d*$/.test(value)) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!String(formData.Amount || "").trim()) {
      alert("Please enter amount.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        ...(() => {
          const paymentMode = paymentModes.find((item) => item.value === normalizeMode(formData.Mode));
          return {
            BankId: Number(paymentMode?.bankId || 0),
            BANKID: Number(paymentMode?.bankId || 0),
            bANKID: Number(paymentMode?.bankId || 0),
            PAYMODE: paymentMode?.label || formData.Mode,
          };
        })(),
        SqlAction: mode === "add" ? "Insert" : "Update",
        ...formData,
        Mode: normalizeMode(formData.Mode),
        Vid: Number(formData.Vid),
        Amount: Number(formData.Amount),
        VrDate: formData.VrDate,
        User: username,
        VrId: mode === "edit" ? initialData.Vrid : null,
      };
      await api.SavesupplierPayment(payload);
      await onSave();
      onClose();
    } catch (err) {
      console.error("Failed to save receipt:", err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal title={mode === "add" ? "Add Receipt" : "Edit Receipt"} isOpen={isOpen} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Suppiler dropdown */}
        <div className="relative">
          <label className="block mb-1 text-sm font-medium">Supplier</label>
          <input
            type="text"
            value={SuppilerSearch}
            onChange={(e) => {
              setSuppilerSearch(e.target.value);
              setIsDropdownOpen(true);
            }}
            onFocus={() => setIsDropdownOpen(true)}
            className="w-full p-2 border rounded"
            placeholder="Search Supplier..."
          />
          {isDropdownOpen && filteredSuppilers.length > 0 && (
            <ul className="absolute z-10 w-full mt-1 overflow-y-auto bg-white border rounded shadow max-h-40">
              {filteredSuppilers.map((c) => (
                <li
                  key={c.Vid}
                  onClick={() => handleSuppilerSelect(c)}
                  className="p-2 cursor-pointer hover:bg-gray-100"
                >
                  {c.Vname} — Bal: ₹{c.Balance}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Prev.Balance */}
        <div>
          <label className="block mb-1 text-sm font-medium">Prev.Balance</label>
          <input
            type="text"
            value={`₹${(formData.Balance || 0).toFixed(2)}`}
            readOnly
            className="w-full p-2 bg-gray-100 border rounded"
          />
        </div>
        {/* Receipt Date */}
        <div>
          <label className="block mb-1 text-sm font-medium">Receipt Date</label>
          <input
            type="date"
            name="VrDate"
            value={formData.VrDate}
            onChange={handleChange}
            className="w-full p-2 border rounded"
          />
        </div>

        {/* Mode */}
        <div>
          <label className="block mb-1 text-sm font-medium">Mode</label>
          <select name="Mode" value={formData.Mode} onChange={handleChange} className="w-full p-2 border rounded">
            {paymentModes.map((paymentMode) => (
              <option key={paymentMode.value} value={paymentMode.value}>
                {paymentMode.label}
              </option>
            ))}
          </select>
        </div>

        {normalizeMode(formData.Mode) === "CHEQUE" && (
          <div>
            <label className="block mb-1 text-sm font-medium">Cheque No</label>
            <input type="text" name="Chqno" value={formData.Chqno} onChange={handleChange} className="w-full p-2 border rounded" />
          </div>
        )}

        {/* Amount */}
        <div>
          <label className="block mb-1 text-sm font-medium">Amount</label>
          <input
            type="text"
            inputMode="decimal"
            name="Amount"
            value={formData.Amount}
            onChange={handleChange}
            className="w-full p-2 border rounded"
          />
        </div>

        {/* Notes */}
        <div>
          <label className="block mb-1 text-sm font-medium">Notes</label>
          <textarea name="Notes" value={formData.Notes} onChange={handleChange} className="w-full p-2 border rounded" />
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-2">
          <Button type="submit" disabled={isSaving} className="px-4 py-2 text-white bg-blue-600 rounded">
            {isSaving ? "Saving..." : "Save"}
          </Button>
          <Button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded">
            Cancel
          </Button>
        </div>
      </form>
    </Modal>
  );
};

/* ---------------- RECEIPT VIEW ---------------- */
const ReceiptView = ({ isOpen, onClose, receipt }) => {
  if (!receipt) return null;

  return (
    <Modal title="Receipt" isOpen={isOpen} onClose={onClose}>
      <div className="space-y-2">
        <div>
          <strong>Supplier:</strong> {receipt.VName}
        </div>
        <div>
          <strong>Date:</strong>{" "}
          {new Date(receipt.VrDate).toLocaleDateString()}
        </div>
        <div>
          <strong>Amount:</strong> ₹{Number(receipt.VrNetTotal).toFixed(2)}
        </div>
        <div>
          <strong>Mode:</strong> {receipt.Mode}
        </div>
        {receipt.ChequeNo && (
          <div>
            <strong>Cheque No:</strong> {receipt.ChequeNo}
          </div>
        )}
        <div>
          <strong>Notes:</strong> {receipt.Remark}
        </div>
      </div>
    </Modal>
  );
};

/* ---------------- MAIN PAGE ---------------- */
function ReceiptsPage() {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("add");
  const [currentReceipt, setCurrentReceipt] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [viewReceipt, setViewReceipt] = useState(null);
  const [viewMode, setViewMode] = useState("card");

  const username = getSessionUsername();

  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.GetSupplierPaymentlistApi(username);

      // console.log("Raw API response:", data);

      // Bind data correctly
      const receiptsData = (data?.Table || []).map((r) => ({
        Vrid: r.Vrid,
        Vrno: r.Vrno || "-",
        VrDate: r.VrDate ? new Date(r.VrDate) : null,
        VId: r.VId || 0,
        VName: r.VName || "-",
        VCode: r.VCode || "-",
        VrNetTotal: r.VrNetTotal || 0,
        Mode: r.Mode || "",
        ChequeNo: r.ChequeNo || "",
        Remark: r.Remark || "",
      }));

      // console.log("Mapped receiptsData:", receiptsData);

      setReceipts(receiptsData);
    } catch (err) {
      console.error("Error fetching receipts:", err);
      setError("Failed to load receipts");
      setReceipts([]);
    } finally {
      setLoading(false);
    }
  }, [username]);


  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  const handleAddClick = () => {
    setFormMode("add");
    setCurrentReceipt(null);
    setIsFormOpen(true);
  };

  const handleReceiptSaved = async () => {
    await fetchReceipts();
    setIsFormOpen(false);
    setCurrentReceipt(null);
    setFormMode("add");
  };

  const handleEditClick = async (vrid) => {
    try {
      const data = await api.Edit_SupplierPaymentApi(vrid);



      const table = data?.Table ?? []; // <-- use data.Table
      if (table.length === 0) {
        // console.error("No receipt data found for vrid:", vrid);
        setError("No receipt data found");
        return;
      }

      setCurrentReceipt(table[0]);
      setFormMode("edit");
      setIsFormOpen(true);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch receipt for editing");
    }
  };


  const handleViewClick = async (vrid) => {
    try {
      const data = await api.Edit_SupplierPaymentApi(vrid);

      // Safely access Table
      const table = data?.EditPaymentAPIjs?.Table ?? [];
      if (table.length === 0) {
        setError("No receipt data found");
        return;
      }

      setViewReceipt(table[0]);
      setIsViewOpen(true);
    } catch (err) {
      console.error(err);
      setError("Failed to fetch receipt for viewing");
    }
  };

  const filtered = receipts.filter(
    (r) =>
      (r.VName || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.Vrno || "").toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="w-full min-h-screen bg-gray-50">
      {/* Header */}
      <div className="px-3 py-3 bg-gray-100 border-b">
        <Heading>Supplier Payments</Heading>

        <div className="flex items-center justify-between mt-3">
          <Button
            className="flex items-center gap-2 px-3 py-1.5 text-white bg-gradient-to-r from-blue-500 to-indigo-600
             rounded-lg shadow-md hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg transition-all duration-300 ease-in-out"
            onClick={handleAddClick}
          >
            <PlusCircle size={18} /> New
          </Button>

          <div className="flex gap-2">
            <button
              onClick={() => setViewMode("card")}
              className={`p-2 rounded ${viewMode === "card"
                  ? "bg-white text-blue-600 shadow"
                  : "text-gray-500"
                }`}
            >
              {/* Card icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4h6v6H4V4z M14 4h6v6h-6V4z M4 14h6v6H4v-6z M14 14h6v6h-6v-6z"
                />
              </svg>
            </button>

            <button
              onClick={() => setViewMode("table")}
              className={`p-2 rounded ${viewMode === "table"
                  ? "bg-white text-blue-600 shadow"
                  : "text-gray-500"
                }`}
            >
              {/* Table icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 10h16M4 14h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-gray">
        <SearchInput
          type="text"
          placeholder="Search Suppliers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* List */}
      <div className="p-3">
        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p className="text-red-600">{error}</p>
        ) : filtered.length === 0 ? (
          <div className="py-12 text-center text-gray-500">No receipts found.</div>
        ) : viewMode === "table" ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  <th className="px-4 py-2 text-sm font-medium text-left text-gray-900 uppercase">
                    Supplier
                  </th>
                  <th className="px-4 py-2 text-sm font-medium text-left text-gray-900 uppercase">
                    Mobile
                  </th>
                  <th className="px-4 py-2 text-sm font-medium text-left text-gray-900 uppercase">
                    Date
                  </th>
                  <th className="px-4 py-2 text-sm font-medium text-right text-gray-900 uppercase">
                    Amount
                  </th>
                  <th className="px-4 py-2 text-sm font-medium text-center text-gray-900 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filtered.map((r) => (
                  <tr key={r.Vrid}>
                    <td className="px-4 py-2">{r.VName}</td>
                    <td className="px-4 py-2">{r.VCode}</td>
                    <td className="px-4 py-2">
                      {new Date(r.VrDate).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-2 text-right">
                      ₹{Number(r.VrNetTotal).toFixed(2)}
                    </td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <Button
                          onClick={() => handleEditClick(r.Vrid)}
                          className="flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium text-white 
                          bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
                          hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
                          transition-all duration-300 ease-in-out"
                        >
                          <FileEdit className="w-5 h-5" />
                          Edit
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((r) => (
              <div
                key={r.Vrid}
                className="p-3 bg-white border rounded-lg shadow-sm"
              >
                <div className="text-sm text-gray-700">
                  <strong>Supplier:</strong> {r.VName}
                </div>
                <div className="text-sm text-gray-700">
                  <strong>Mobile:</strong> {r.VCode}
                </div>
                <div className="text-sm text-gray-700">
                  <strong>Date:</strong>{" "}
                  {new Date(r.VrDate).toLocaleDateString()}
                </div>
                <div className="text-sm text-gray-700">
                  <strong>Amount:</strong> ₹{Number(r.VrNetTotal).toFixed(2)}
                </div>
                <div className="flex gap-2 mt-3">
                  <Button
                    onClick={() => handleEditClick(r.Vrid)}
                    className="flex items-center justify-center gap-2 px-3 py-1.5 text-xs font-medium text-white 
                    bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg shadow-md 
                    hover:from-blue-600 hover:to-indigo-700 hover:shadow-lg 
                    transition-all duration-300 ease-in-out"
                  >
                    <FileEdit className="w-5 h-5" />
                    Edit
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ReceiptForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSave={handleReceiptSaved}
        mode={formMode}
        initialData={currentReceipt}
      />

      <ReceiptView
        isOpen={isViewOpen}
        onClose={() => setIsViewOpen(false)}
        receipt={viewReceipt}
      />
    </div>
  );
}

export default ReceiptsPage;
